import { useState, useEffect } from "react";
import "./DailyLog.css";

const categoryOptions = {
  Work: {
    taskTypes: ["Development", "Testing", "Documentation", "Meeting", "Code Review", "Debugging"],
    achievements: ["Bug fixed", "Feature deployed", "Documentation updated", "Code reviewed", "Performance improved"],
    challenges: ["Blocked by dependency", "Time constraints", "Technical issue", "Resource unavailable", "Learning curve"]
  },
  Learning: {
    taskTypes: ["Reading", "Practice", "Study", "Project Building", "Course Work"],
    achievements: ["Module completed", "Problem solved", "Project built", "Concept mastered", "Skills improved"],
    challenges: ["Difficult concept", "Time management", "Resource limitations", "Motivation", "Technical barriers"]
  },
  SelfDevelopment: {
    taskTypes: ["Meditation", "Reading", "Journaling", "Networking", "Reflection"],
    achievements: ["Streak maintained", "Daily entries", "Connections made", "Insights gained", "Growth achieved"],
    challenges: ["Lack of consistency", "Time constraints", "Low motivation", "External distractions", "Habit formation"]
  },
  Fitness: {
    taskTypes: ["Cardio", "Strength", "Flexibility", "Sports", "Recovery"],
    achievements: ["Personal record", "Workout completed", "Distance achieved", "Strength gained", "Improvement noted"],
    challenges: ["Fatigue", "Muscle soreness", "Weather", "Motivation issue", "Time conflict"]
  }
};

export default function DailyLog() {
  const [entry, setEntry] = useState({
    category: "Work",
    date: new Date().toISOString().split('T')[0],
    topic: "",
    taskType: "",
    tasks: "",
    achievement: "",
    challenges: "",
    hours: "",
    notes: ""
  });
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState("All");

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    fetch("http://localhost:5000/api/daily", {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setLogs(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEntry({ ...entry, [name]: value });
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setEntry({ ...entry, category, taskType: "", achievement: "", challenges: "" });
  };

  const handleSubmit = async () => {
    if (!entry.date || !entry.topic || !entry.taskType) {
      alert("Please fill in required fields (Date, Topic, Task Type)");
      return;
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await fetch("http://localhost:5000/api/daily", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(entry),
      });
      const data = await res.json();
      setLogs([...logs, data]);
      setEntry({ category: "Work", date: new Date().toISOString().split('T')[0], topic: "", taskType: "", tasks: "", achievement: "", challenges: "", hours: "", notes: "" });
      setShowForm(false);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredLogs = filterCategory === "All" ? logs : logs.filter(log => log.category === filterCategory);

  return (
    <div className="daily-log-container">
      <div className="daily-header">
        <h2>📝 Daily Progress Log</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ New Entry"}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Create Daily Entry</h3>
          
          {/* Category Selection */}
          <div className="form-group">
            <label>Category *</label>
            <div className="category-buttons">
              {Object.keys(categoryOptions).map(cat => (
                <button
                  key={cat}
                  className={`category-btn ${entry.category === cat ? "active" : ""}`}
                  onClick={() => handleCategoryChange({ target: { value: cat } })}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input type="date" name="date" value={entry.date} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Hours</label>
              <input type="number" name="hours" value={entry.hours} onChange={handleChange} min="0" max="24" placeholder="0-24" />
            </div>
          </div>

          <div className="form-group">
            <label>Topic *</label>
            <input name="topic" value={entry.topic} onChange={handleChange} placeholder="What did you work on?" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Task Type *</label>
              <select name="taskType" value={entry.taskType} onChange={handleChange}>
                <option value="">Select type...</option>
                {categoryOptions[entry.category].taskTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            {entry.taskType === "Custom" && (
              <div className="form-group">
                <label>Custom Task</label>
                <input name="tasks" value={entry.tasks} onChange={handleChange} placeholder="Specify..." />
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Achievement</label>
              <select name="achievement" value={entry.achievement} onChange={handleChange}>
                <option value="">Select...</option>
                {categoryOptions[entry.category].achievements.map(ach => (
                  <option key={ach} value={ach}>{ach}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Challenges</label>
              <select name="challenges" value={entry.challenges} onChange={handleChange}>
                <option value="">Select...</option>
                {categoryOptions[entry.category].challenges.map(ch => (
                  <option key={ch} value={ch}>{ch}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea name="notes" value={entry.notes} onChange={handleChange} placeholder="Additional details..." rows="3" />
          </div>

          <button className="btn-submit" onClick={handleSubmit}>Save Entry</button>
        </div>
      )}

      <div className="filter-section">
        <label>Filter by Category:</label>
        <div className="filter-buttons">
          {["All", "Work", "Learning", "SelfDevelopment", "Fitness"].map(cat => (
            <button
              key={cat}
              className={`filter-btn ${filterCategory === cat ? "active" : ""}`}
              onClick={() => setFilterCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="logs-section">
        <h3>Recent Entries ({filteredLogs.length})</h3>
        {filteredLogs.length === 0 ? (
          <p className="empty-state">No entries yet. Create your first daily log!</p>
        ) : (
          <div className="logs-grid">
            {filteredLogs.map((log, i) => (
              <div key={i} className={`log-card category-${log.category?.toLowerCase() || "work"}`}>
                <div className="log-header">
                  <span className="badge">{log.category || "Work"}</span>
                  <span className="date">{new Date(log.date).toLocaleDateString()}</span>
                </div>
                <h4>{log.topic}</h4>
                <div className="log-details">
                  <p><strong>Type:</strong> {log.taskType}</p>
                  {log.hours && <p><strong>Hours:</strong> {log.hours}h</p>}
                  {log.achievement && <p><strong>Achievement:</strong> {log.achievement}</p>}
                  {log.challenges && <p><strong>Challenge:</strong> {log.challenges}</p>}
                  {log.notes && <p><strong>Notes:</strong> {log.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}